/**
 * Role-labelled building parts and the two archetypes that change massing —
 * tower and porch (issue #122, roadmap batch 4 §2a/§2b). #114 already decomposes
 * a footprint into lobes and roofs each one; this module labels the load-bearing
 * cases by role and produces the extra masses the renderer appends:
 *   - a `tower` (a compact lobe extruded to its own measured height with a
 *     pyramidal / spire cap), and
 *   - a `porch` (a small mass offset in front of the primary facade with its own
 *     low roof).
 * Every other role renders as today's #114 lobe — this module adds nothing for
 * it.
 *
 * Assembly happens at VIEWER BOOT TIME (the issue's option 2): `decomposeFootprint`
 * already runs in the viewer, so `lobe_index` is meaningful against the live
 * decomposition without replicating that geometry in Python. The committed
 * `parts[]` block (roadmap §3, reserved) is read structured-first when present;
 * where it is absent — the whole corpus today — a tower is DERIVED from the
 * mapped-use gate plus the measured `heights.tower` (#119). The mapping is pure
 * and deterministic, and is documented in docs/architecture.md.
 *
 * Pure, three.js-free, unit-tested directly in Node — mirrors roofLobes.ts and
 * terraceSegments.ts. Nothing here reaches raypath/ or any LOS result (hard
 * constraint #1, guarded by buildings.guard.test.ts), and no height is ever
 * invented: a tower's height is a measured LiDAR value or a documented
 * provenance-ladder fallback (classified storeys → nominal), never fabricated
 * (hard constraints #6/#7).
 *
 * WHY THE TOWER GATE IS CONSERVATIVE. #119 recorded that the corpus has 86
 * `heights.tower` candidates and MANY ARE VEGETATION — a First-Return DSM cannot
 * tell a masonry bell tower from tree canopy over a footprint. Only ONE of the 86
 * is a real tower (All Saints church, the single `building=church` among them).
 * So raw `heights.tower` presence must NEVER trigger a tower. The gate requires an
 * ecclesiastical / civic / education USE on the mapped rung (the OSM `building=`
 * tag, since `building_use` is null across the corpus) OR an explicit `parts[]`
 * `role: "tower"` entry. That is what keeps the scene from sprouting 85 spurious
 * spires.
 */
import type { LocalXZ } from '../core/coordinates';
import { orientedBbox } from './roofLobes';

// --- roles --------------------------------------------------------------------

export type PartRole = 'main' | 'wing' | 'porch' | 'tower' | 'extension' | 'garage' | 'outbuilding';

export const PART_ROLES: ReadonlySet<string> = new Set<PartRole>([
  'main',
  'wing',
  'porch',
  'tower',
  'extension',
  'garage',
  'outbuilding',
]);

// --- tower use gate -----------------------------------------------------------

/**
 * OSM `building=` values that mark a building as ecclesiastical / civic /
 * education — the mapped-rung signal that a measured tall element is a real
 * tower, not vegetation. `building_use` is null across the corpus, so the OSM tag
 * is the only reliable signal (roadmap §2a: "the OSM tag building=church
 * (mapped). This is decisive.").
 */
export const TOWER_USE_OSM_BUILDING: ReadonlySet<string> = new Set([
  // ecclesiastical
  'church',
  'chapel',
  'cathedral',
  'monastery',
  'mosque',
  'temple',
  'synagogue',
  'shrine',
  // civic
  'civic',
  'townhall',
  'town_hall',
  'public',
  'government',
  // education
  'school',
  'college',
  'university',
]);

/** `building_use.value` (or bare string) values that satisfy the gate, mirroring
 * the expanded vocabulary in the roadmap §3 schema. */
export const TOWER_USE_VALUES: ReadonlySet<string> = new Set(['ecclesiastical', 'civic', 'education']);

/**
 * True when a building's use permits a tower archetype. Passes on an
 * ecclesiastical/civic/education OSM `building=` tag, OR a `building_use` in the
 * allowed set (accepting both a bare string and the schema's `{ value }` object).
 * Never throws on a malformed `building_use` (hard constraint #7) — an
 * unrecognised shape simply fails the gate.
 */
export function resolveTowerUseAllowed(osmBuilding: string | undefined, buildingUse: unknown): boolean {
  if (osmBuilding != null && TOWER_USE_OSM_BUILDING.has(osmBuilding)) return true;
  const value =
    typeof buildingUse === 'string'
      ? buildingUse
      : buildingUse != null && typeof buildingUse === 'object' && typeof (buildingUse as { value?: unknown }).value === 'string'
        ? ((buildingUse as { value: string }).value)
        : null;
  return value != null && TOWER_USE_VALUES.has(value);
}

// --- tuning constants ---------------------------------------------------------

/** Maximum side (m) of a synthesized tower footprint square — a bell tower is
 * compact, never the whole nave. */
export const TOWER_MAX_SIDE_M = 7;
/** A synthesized tower square narrower than this is not raised (avoids a needle
 * tower over a thin footprint). */
export const TOWER_MIN_SIDE_M = 2;
/** A measured tower top must clear the main ridge by at least this (m) to count
 * as a tower — a "tower" barely above the ridge is not a recognisable tower and
 * guards against a marginal DSM reading. */
export const TOWER_MIN_PROMINENCE_M = 2;
/** Pyramidal cap rise as a fraction of the tower's short side — a proper spire. */
export const TOWER_CAP_RISE_FRAC = 0.9;

/** Porch mass dimensions (m): a small lean-to-ish box in front of the door. */
export const PORCH_WIDTH_M = 1.8;
export const PORCH_DEPTH_M = 1.6;
export const PORCH_EAVES_M = 2.2;
export const PORCH_RIDGE_RISE_M = 0.9;

// --- assembled outputs --------------------------------------------------------

export type PartProvenance = 'measured' | 'classified' | 'default';

/** A tower archetype to add: extrude `footprint` from ground to `topM`, then a
 * pyramidal cap rising `capRiseM` above it. */
export interface TowerPart {
  role: 'tower';
  /** the ring to extrude (a real lobe when lobeIndex >= 0, else a synthesized square) */
  footprint: LocalXZ[];
  /** index into decomposeFootprint() lobes, or -1 when synthesized */
  lobeIndex: number;
  /** wall top height (m), local building space (0 = ground) */
  topM: number;
  /** pyramidal cap rise (m) above topM */
  capRiseM: number;
  provenance: PartProvenance;
  confidence: number;
}

/** A porch archetype to add: a small mass in front of the primary facade with a
 * low gabled roof (eaves at `eavesM`, ridge at `ridgeM`). */
export interface PorchPart {
  role: 'porch';
  footprint: LocalXZ[];
  eavesM: number;
  ridgeM: number;
  provenance: PartProvenance;
  confidence: number;
}

export type AssembledPart = TowerPart | PorchPart;

/** Provenance-ladder fallback heights the assembler uses when a part carries no
 * measured height — computed once by the caller so this module stays free of the
 * height-precedence constants in buildings.ts. */
export interface HeightLadder {
  /** classified storeys * storey height (m), or null when no usable storeys */
  classified: number | null;
  /** nominal fallback (m) — the building's own resolved eaves */
  nominal: number;
}

/** Everything the (pure) assembler needs, computed by BuildingsLayer from the
 * same helpers the single-mass path already uses. */
export interface PartsContext {
  osmBuilding?: string;
  appearance?: { building_use?: unknown; heights?: unknown; parts?: unknown };
  /** footprint ring in local x/z (open) */
  ringLocal: LocalXZ[];
  /** decomposeFootprint(ringLocal) — the #114 lobes an explicit lobe_index indexes into */
  lobes: LocalXZ[][];
  /** the primary (longest-edge) facade a porch projects from, or null */
  primaryEdge: { a: LocalXZ; b: LocalXZ } | null;
  /** footprint centroid, for orienting a porch outward */
  interior: LocalXZ;
  /** resolved main eaves height (m) */
  eavesM: number;
  /** measured main ridge (m) if any, for the tower prominence check */
  ridgeM: number | null;
  /** provenance-ladder fallback heights for a null-height part */
  heightLadder: HeightLadder;
  /** deterministic per-building seed (footprint index) */
  seed: number;
}

// --- structured warnings ------------------------------------------------------

/** A single structured warning for an ignored/deduplicated part (failure-table
 * rows). Kept to console.warn — the viewer has no logger — and only ever fires on
 * malformed EXPLICIT parts[] data, which the real corpus never carries. */
function warnPart(reason: string, detail: Record<string, unknown>): void {
  // eslint-disable-next-line no-console
  console.warn(JSON.stringify({ event: 'building-part-ignored', reason, ...detail }));
}

// --- raw parts[] parsing ------------------------------------------------------

interface ParsedPart {
  role: PartRole;
  /** valid integer lobe_index, or null when absent */
  lobeIndex: number | null;
  /** true when lobe_index was present but not a valid integer (malformed → skip) */
  lobeIndexMalformed: boolean;
  height: unknown;
  confidence: number;
}

function finiteInRange01(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1 ? v : fallback;
}

function parseRawPart(raw: unknown): ParsedPart | null {
  if (raw == null || typeof raw !== 'object') return null;
  const rec = raw as { role?: unknown; lobe_index?: unknown; height?: unknown; confidence?: unknown };
  if (typeof rec.role !== 'string' || !PART_ROLES.has(rec.role)) return null;
  const li = rec.lobe_index;
  let lobeIndex: number | null = null;
  let lobeIndexMalformed = false;
  if (li !== undefined && li !== null) {
    if (typeof li === 'number' && Number.isInteger(li)) lobeIndex = li;
    else lobeIndexMalformed = true;
  }
  return {
    role: rec.role as PartRole,
    lobeIndex,
    lobeIndexMalformed,
    height: rec.height,
    confidence: finiteInRange01(rec.confidence, 0.6),
  };
}

/** Reads an explicit part `height` block into a top height + provenance, falling
 * back down the ladder (classified storeys → nominal) when it is null/missing.
 * Never fabricates a `measured` value (failure-table row + hard constraint #7). */
function resolvePartTop(height: unknown, ladder: HeightLadder): { topM: number; provenance: PartProvenance } {
  if (height != null && typeof height === 'object') {
    const h = height as { top_m?: unknown; ridge_m?: unknown; provenance?: unknown };
    const raw = typeof h.top_m === 'number' && Number.isFinite(h.top_m) ? h.top_m : typeof h.ridge_m === 'number' && Number.isFinite(h.ridge_m) ? h.ridge_m : null;
    if (raw != null && raw > 0) {
      const prov =
        h.provenance === 'measured' || h.provenance === 'classified' || h.provenance === 'default'
          ? (h.provenance as PartProvenance)
          : 'classified';
      return { topM: raw, provenance: prov };
    }
  }
  if (ladder.classified != null) return { topM: ladder.classified, provenance: 'classified' };
  return { topM: ladder.nominal, provenance: 'default' };
}

// --- footprint synthesis ------------------------------------------------------

/** The four corners of an oriented box (ring order), local x/z. */
function boxRing(centre: LocalXZ, dir: { x: number; z: number }, halfLong: number, halfShort: number): LocalXZ[] {
  const perp = { x: -dir.z, z: dir.x };
  const c = (along: number, across: number): LocalXZ => ({
    x: centre.x + dir.x * along * halfLong + perp.x * across * halfShort,
    z: centre.z + dir.z * along * halfLong + perp.z * across * halfShort,
  });
  return [c(-1, -1), c(1, -1), c(1, 1), c(-1, 1)];
}

/**
 * Synthesizes a compact square tower footprint at one end of a footprint's
 * oriented long axis. Used when no explicit `lobe_index` names the tower lobe —
 * the case for every church in the corpus, whose footprints are plain rectangles
 * with no separately-mapped tower. The square's side is the footprint's short
 * span, capped at TOWER_MAX_SIDE_M; it is placed at the LOWER-along end
 * (deterministic). Returns null for a footprint too thin to carry a tower.
 *
 * Placement is illustrative massing, in the same category as the existing
 * procedural roofs/chimneys ("illustrative proportions only"): the tower's
 * EXISTENCE and HEIGHT are measured (LiDAR), only its position along a plain
 * rectangular footprint is stylised. It never influences LOS (hard constraint #1).
 */
export function synthesizeTowerFootprint(
  ringLocal: LocalXZ[],
  _lobes?: LocalXZ[][],
): { footprint: LocalXZ[]; lobeIndex: number } | null {
  if (ringLocal.length < 3) return null;
  const box = orientedBbox(ringLocal);
  const side = Math.min(2 * box.halfSpan, TOWER_MAX_SIDE_M);
  if (!(side >= TOWER_MIN_SIDE_M)) return null;
  const half = side / 2;
  const along = box.halfLength > half ? box.halfLength - half : 0;
  const centre = { x: box.centre.x - box.dir.x * along, z: box.centre.z - box.dir.z * along };
  return { footprint: boxRing(centre, box.dir, half, half), lobeIndex: -1 };
}

/**
 * A small rectangular porch footprint projecting outward from a facade edge at
 * its midpoint, oriented AWAY from the footprint interior (same outward-normal
 * flip as buildPrimaryFacadeDetail). Pure and deterministic.
 */
export function porchFootprint(
  edgeA: LocalXZ,
  edgeB: LocalXZ,
  interior: LocalXZ,
  widthM = PORCH_WIDTH_M,
  depthM = PORCH_DEPTH_M,
): LocalXZ[] {
  const dx = edgeB.x - edgeA.x;
  const dz = edgeB.z - edgeA.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-6) return [];
  const ax = dx / len;
  const az = dz / len;
  let nx = -az;
  let nz = ax;
  const midX = (edgeA.x + edgeB.x) / 2;
  const midZ = (edgeA.z + edgeB.z) / 2;
  if (nx * (interior.x - midX) + nz * (interior.z - midZ) > 0) {
    nx = -nx;
    nz = -nz;
  }
  const hw = widthM / 2;
  const p0: LocalXZ = { x: midX - ax * hw, z: midZ - az * hw };
  const p1: LocalXZ = { x: midX + ax * hw, z: midZ + az * hw };
  const p2: LocalXZ = { x: p1.x + nx * depthM, z: p1.z + nz * depthM };
  const p3: LocalXZ = { x: p0.x + nx * depthM, z: p0.z + nz * depthM };
  return [p0, p1, p2, p3];
}

// --- assembly -----------------------------------------------------------------

function towerCapRise(footprint: LocalXZ[]): number {
  const box = orientedBbox(footprint);
  const shortSide = 2 * Math.min(box.halfLength, box.halfSpan);
  return shortSide * TOWER_CAP_RISE_FRAC;
}

/** Derives a tower purely from the mapped-use gate + measured `heights.tower`,
 * for the corpus case where no explicit `parts[]` entry exists. Returns null
 * unless the use gate passes AND a measured tower top clears the main ridge by
 * the prominence floor. */
function deriveTower(ctx: PartsContext): TowerPart | null {
  if (!resolveTowerUseAllowed(ctx.osmBuilding, ctx.appearance?.building_use)) return null;
  const heights = ctx.appearance?.heights;
  if (heights == null || typeof heights !== 'object') return null;
  const h = heights as { tower?: unknown; confidence?: unknown };
  if (h.tower == null || typeof h.tower !== 'object') return null;
  const topRaw = (h.tower as { top_m?: unknown }).top_m;
  if (typeof topRaw !== 'number' || !Number.isFinite(topRaw) || topRaw <= 0) return null;
  const mainTop = ctx.ridgeM ?? ctx.eavesM;
  if (topRaw <= mainTop + TOWER_MIN_PROMINENCE_M) return null;
  const syn = synthesizeTowerFootprint(ctx.ringLocal, ctx.lobes);
  if (!syn) return null;
  return {
    role: 'tower',
    footprint: syn.footprint,
    lobeIndex: syn.lobeIndex,
    topM: topRaw,
    capRiseM: towerCapRise(syn.footprint),
    provenance: 'measured',
    confidence: finiteInRange01(h.confidence, 0.8),
  };
}

/**
 * Assembles the role-labelled archetype parts (tower + porch) to ADD to a
 * building, deterministically. Explicit `parts[]` entries are read
 * structured-first; when none produce a tower, one is derived from the use gate +
 * measured height. Returns [] for any building that does not qualify — the whole
 * corpus of plain dwellings — so the renderer's main-mass path is byte-identical
 * to today's #114 output (a tower/porch is purely additive geometry).
 *
 * Never throws on malformed input (hard constraint #7): a bad `parts[]` shape,
 * junk role, or malformed lobe_index yields no archetype rather than a guess.
 */
export function assembleParts(ctx: PartsContext): AssembledPart[] {
  const out: AssembledPart[] = [];
  const claimedLobes = new Set<number>();
  let hasExplicitTower = false;

  const rawParts = ctx.appearance?.parts;
  if (Array.isArray(rawParts)) {
    for (const raw of rawParts) {
      const parsed = parseRawPart(raw);
      if (!parsed) continue;

      if (parsed.role === 'tower') {
        if (parsed.lobeIndexMalformed) continue; // malformed index → no guess
        let footprint: LocalXZ[];
        let lobeIndex: number;
        if (parsed.lobeIndex != null) {
          if (parsed.lobeIndex < 0 || parsed.lobeIndex >= ctx.lobes.length) {
            warnPart('tower-lobe-index-out-of-range', { lobeIndex: parsed.lobeIndex, lobes: ctx.lobes.length });
            continue; // failure-table: ignore; the lobe renders unlabelled. Never guess a different lobe.
          }
          if (claimedLobes.has(parsed.lobeIndex)) {
            warnPart('duplicate-lobe-index', { lobeIndex: parsed.lobeIndex, role: 'tower' });
            continue; // failure-table: deterministic winner is the first; loser dropped.
          }
          claimedLobes.add(parsed.lobeIndex);
          footprint = ctx.lobes[parsed.lobeIndex];
          lobeIndex = parsed.lobeIndex;
        } else {
          const syn = synthesizeTowerFootprint(ctx.ringLocal, ctx.lobes);
          if (!syn) continue;
          footprint = syn.footprint;
          lobeIndex = syn.lobeIndex;
        }
        const { topM, provenance } = resolvePartTop(parsed.height, ctx.heightLadder);
        if (!(topM > 0)) continue;
        out.push({
          role: 'tower',
          footprint,
          lobeIndex,
          topM,
          capRiseM: towerCapRise(footprint),
          provenance,
          confidence: parsed.confidence,
        });
        hasExplicitTower = true;
      } else if (parsed.role === 'porch') {
        if (!ctx.primaryEdge) continue; // no facade to project a porch from
        const footprint = porchFootprint(ctx.primaryEdge.a, ctx.primaryEdge.b, ctx.interior);
        if (footprint.length < 3) continue;
        const eavesM = Math.min(PORCH_EAVES_M, ctx.eavesM * 0.7);
        out.push({
          role: 'porch',
          footprint,
          eavesM,
          ridgeM: eavesM + PORCH_RIDGE_RISE_M,
          provenance: 'classified',
          confidence: parsed.confidence,
        });
      }
      // every other role renders as today's #114 lobe — nothing added here.
    }
  }

  if (!hasExplicitTower) {
    const derived = deriveTower(ctx);
    if (derived) out.push(derived);
  }
  return out;
}

// --- pure geometry builders ---------------------------------------------------

interface Point3 {
  x: number;
  y: number;
  z: number;
}

function pushTri(out: number[], a: Point3, b: Point3, c: Point3): void {
  out.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
}

/**
 * A pyramidal (spire) cap over a tower footprint: four triangular faces from the
 * oriented-bbox top corners at `baseY` up to a single apex at the box centre,
 * `capRiseM` above. Winding is immaterial (the cap material is DoubleSide and
 * normals are recomputed). Pure; empty for a degenerate footprint.
 */
export function buildTowerCap(footprint: LocalXZ[], baseY: number, capRiseM: number): Float32Array {
  if (footprint.length < 3) return new Float32Array(0);
  const box = orientedBbox(footprint);
  const corners = boxRing(box.centre, box.dir, box.halfLength, box.halfSpan);
  const apex: Point3 = { x: box.centre.x, y: baseY + capRiseM, z: box.centre.z };
  const out: number[] = [];
  for (let i = 0; i < 4; i++) {
    const a = corners[i];
    const b = corners[(i + 1) % 4];
    pushTri(out, { x: a.x, y: baseY, z: a.z }, { x: b.x, y: baseY, z: b.z }, apex);
  }
  return new Float32Array(out);
}

/**
 * A porch mass: vertical walls extruded 0 -> `eavesM` around the porch footprint
 * (with metre-space UVs like buildWalls), plus a low gabled roof rising to
 * `ridgeM`. Returned as separate wall/roof position arrays so the caller can draw
 * walls with the wall material and the roof with the roof material — no new
 * material keys. Pure; empty for a degenerate footprint.
 */
export function buildPorchMass(
  footprint: LocalXZ[],
  eavesM: number,
  ridgeM: number,
): { positions: Float32Array; uvs: Float32Array; roof: Float32Array } {
  if (footprint.length < 3 || !(eavesM > 0)) {
    return { positions: new Float32Array(0), uvs: new Float32Array(0), roof: new Float32Array(0) };
  }
  const positions: number[] = [];
  const uvs: number[] = [];
  const n = footprint.length;
  for (let i = 0; i < n; i++) {
    const a = footprint[i];
    const b = footprint[(i + 1) % n];
    const edgeLength = Math.hypot(b.x - a.x, b.z - a.z);
    positions.push(a.x, 0, a.z, b.x, 0, b.z, b.x, eavesM, b.z);
    positions.push(a.x, 0, a.z, b.x, eavesM, b.z, a.x, eavesM, a.z);
    uvs.push(0, 0, edgeLength, 0, edgeLength, eavesM);
    uvs.push(0, 0, edgeLength, eavesM, 0, eavesM);
  }

  const box = orientedBbox(footprint);
  const perp = { x: -box.dir.z, z: box.dir.x };
  const ridgeApex = Math.max(ridgeM, eavesM);
  const ridgeA: Point3 = { x: box.centre.x - box.dir.x * box.halfLength, y: ridgeApex, z: box.centre.z - box.dir.z * box.halfLength };
  const ridgeB: Point3 = { x: box.centre.x + box.dir.x * box.halfLength, y: ridgeApex, z: box.centre.z + box.dir.z * box.halfLength };
  const eave = (along: number, across: number): Point3 => ({
    x: box.centre.x + box.dir.x * along * box.halfLength + perp.x * across * box.halfSpan,
    y: eavesM,
    z: box.centre.z + box.dir.z * along * box.halfLength + perp.z * across * box.halfSpan,
  });
  const eaveA1 = eave(-1, 1);
  const eaveA2 = eave(-1, -1);
  const eaveB1 = eave(1, 1);
  const eaveB2 = eave(1, -1);
  const roof: number[] = [];
  pushTri(roof, eaveA1, eaveB1, ridgeB);
  pushTri(roof, eaveA1, ridgeB, ridgeA);
  pushTri(roof, eaveB2, eaveA2, ridgeA);
  pushTri(roof, eaveB2, ridgeA, ridgeB);
  pushTri(roof, eaveA2, eaveA1, ridgeA);
  pushTri(roof, eaveB1, eaveB2, ridgeB);

  return { positions: new Float32Array(positions), uvs: new Float32Array(uvs), roof: new Float32Array(roof) };
}
